import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, Copy, Check, AlertTriangle } from 'lucide-react';
import { useTheme } from './ThemeProvider';

/**
 * MFASettings
 * 
 * Toggle MFA on/off from user settings/profile.
 * Shows backup codes when first enabled.
 * 
 * Props:
 *   apiUrl  - Your API base URL
 *   token   - User's access token (from localStorage)
 */
export default function MFASettings({ apiUrl, token }) {
  const { theme } = useTheme();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [remainingBackupCodes, setRemainingBackupCodes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  // Fetch MFA status on mount
  useEffect(() => {
    fetchMFAStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMFAStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/auth/mfa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMfaEnabled(data.data.mfaEnabled);
        setRemainingBackupCodes(data.data.remainingBackupCodes);
      }
    } catch (err) {
      console.error('Failed to fetch MFA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    setActionLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiUrl}/auth/mfa/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ method: 'email' })
      });

      const data = await response.json();

      if (data.success) {
        setMfaEnabled(true);
        setBackupCodes(data.data.backupCodes);
        setRemainingBackupCodes(data.data.backupCodes.length);
        setSuccess('Two-factor authentication enabled!');
      } else {
        setError(data.message || 'Failed to enable MFA');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      setError('Please enter your password.');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiUrl}/auth/mfa/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: disablePassword })
      });

      const data = await response.json();

      if (data.success) {
        setMfaEnabled(false);
        setShowDisableConfirm(false);
        setDisablePassword('');
        setBackupCodes(null);
        setSuccess('Two-factor authentication disabled.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to disable MFA');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (!backupCodes) return;
    const text = backupCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading security settings...
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--surface)',
      borderRadius: '16px',
      padding: '24px',
      border: '1px solid var(--border)',
      marginBottom: '16px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: mfaEnabled 
            ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {mfaEnabled ? <ShieldCheck size={20} color="white" /> : <Shield size={20} color="white" />}
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>
            Two-Factor Authentication
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
            {mfaEnabled ? 'Enabled — your account has extra protection' : 'Add an extra layer of security to your account'}
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          color: theme === 'dark' ? '#fca5a5' : '#dc2626',
          padding: '10px 12px',
          borderRadius: '8px',
          marginBottom: '12px',
          fontSize: '13px',
          border: '1px solid rgba(239, 68, 68, 0.2)'
        }}>
          {error}
        </div>
      )}
      {success && !backupCodes && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          color: theme === 'dark' ? '#86efac' : '#059669',
          padding: '10px 12px',
          borderRadius: '8px',
          marginBottom: '12px',
          fontSize: '13px',
          border: '1px solid rgba(34, 197, 94, 0.2)'
        }}>
          {success}
        </div>
      )}

      {/* Backup codes display (shown once after enabling) */}
      {backupCodes && (
        <div style={{
          background: theme === 'dark' ? 'rgba(251, 191, 36, 0.1)' : '#fffbeb',
          border: `1px solid ${theme === 'dark' ? 'rgba(251, 191, 36, 0.3)' : '#fde68a'}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <AlertTriangle size={18} color={theme === 'dark' ? '#fbbf24' : '#d97706'} />
            <strong style={{ fontSize: '14px', color: theme === 'dark' ? '#fde047' : '#92400e' }}>
              Save your backup codes
            </strong>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--text-muted)' }}>
            If you lose access to your email, you can use these codes to sign in. Each code can only be used once. Save them somewhere safe — you won't see them again.
          </p>
          
          <div style={{
            background: theme === 'dark' ? 'rgba(0,0,0,0.3)' : 'white',
            borderRadius: '8px',
            padding: '16px',
            fontFamily: '"Courier New", monospace',
            fontSize: '15px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            marginBottom: '12px'
          }}>
            {backupCodes.map((code, i) => (
              <div key={i} style={{
                padding: '4px 8px',
                background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#f8f9fa',
                borderRadius: '4px',
                textAlign: 'center',
                letterSpacing: '1px',
                color: 'var(--text-primary)'
              }}>
                {code}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={copyBackupCodes}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            {copiedCodes ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy all codes</>}
          </button>

          <button
            type="button"
            onClick={() => setBackupCodes(null)}
            style={{
              display: 'block',
              margin: '12px auto 0',
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            I've saved my codes
          </button>
        </div>
      )}

      {/* Enable / Disable button */}
      {!mfaEnabled ? (
        <button
          type="button"
          onClick={handleEnable}
          disabled={actionLoading}
          style={{
            width: '100%',
            padding: '12px',
            background: actionLoading ? '#95a5a6' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: actionLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {actionLoading ? 'Enabling...' : 'Enable Two-Factor Authentication'}
        </button>
      ) : !showDisableConfirm ? (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '10px',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Backup codes remaining: <strong style={{ color: remainingBackupCodes <= 2 ? '#ef4444' : '#2c3e50' }}>{remainingBackupCodes}</strong>
            </span>
            {remainingBackupCodes <= 2 && (
              <span style={{ fontSize: '12px', color: theme === 'dark' ? '#fca5a5' : '#ef4444' }}>
                ⚠️ Running low
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowDisableConfirm(true)}
            style={{
              width: '100%',
              padding: '12px',
              background: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'white',
              color: theme === 'dark' ? '#fca5a5' : '#ef4444',
              border: `1px solid ${theme === 'dark' ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Disable Two-Factor Authentication
          </button>
        </div>
      ) : (
        <div style={{
          background: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
          border: `1px solid ${theme === 'dark' ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
          borderRadius: '10px',
          padding: '16px'
        }}>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: theme === 'dark' ? '#fca5a5' : '#991b1b' }}>
            Enter your password to confirm disabling MFA. This will make your account less secure.
          </p>
          <input
            type="password"
            placeholder="Enter your password"
            value={disablePassword}
            onChange={(e) => { setDisablePassword(e.target.value); setError(''); }}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${theme === 'dark' ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
              borderRadius: '8px',
              fontSize: '14px',
              marginBottom: '12px',
              outline: 'none',
              boxSizing: 'border-box',
              background: theme === 'dark' ? 'rgba(0,0,0,0.3)' : 'white',
              color: 'var(--text-primary)'
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => { setShowDisableConfirm(false); setDisablePassword(''); setError(''); }}
              style={{
                flex: 1,
                padding: '10px',
                background: 'var(--surface)',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDisable}
              disabled={actionLoading}
              style={{
                flex: 1,
                padding: '10px',
                background: actionLoading ? '#95a5a6' : '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: actionLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {actionLoading ? 'Disabling...' : 'Confirm Disable'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}